import {
  Directive,
  OnDestroy,
  HostListener,
  Input,
  Renderer2,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subject, Observable } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { Organization, Account, Document, Client } from '../sdk';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../services/language.service';

@Directive({
  selector: '[appSmartcodePreview]',
})
export class SmartcodePreviewDirective implements OnDestroy, AfterViewInit {
  @Input() account: Account;
  @Input() document: Document;
  @Input() organization: Organization;
  @Input() refresh: Observable<any>;
  private changed: string;
  private definitions: any;
  private fakeTextarea: ElementRef;
  private onDestroy$: Subject<void> = new Subject();
  private whitelisted: string[] = ['tax_clause'];

  constructor(
    private control: NgControl,
    private el: ElementRef,
    private languageService: LanguageService,
    private renderer: Renderer2,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.fakeTextarea = this.renderer.createElement('div');
    this.renderer.addClass(this.fakeTextarea, 'fake-textarea');
    this.renderer.addClass(this.fakeTextarea, 'whitespace-pre');
    this.renderer.setStyle(this.fakeTextarea, 'cursor', 'text');

    this.definitions = {
      organization: Organization.getModelDefinition(),
      document: Document.getModelDefinition(),
      account: Account.getModelDefinition(),
      client: Client.getModelDefinition(),
    };

    this.autoHeight(this.el.nativeElement, 1);

    for (const c of this.el.nativeElement.classList) {
      this.renderer.addClass(this.fakeTextarea, c);
    }

    this.renderer.insertBefore(
      this.el.nativeElement.parentElement,
      this.fakeTextarea,
      this.el.nativeElement,
    );
    this.renderer.listen(this.fakeTextarea, 'click', this.onClick.bind(this));

    if (this.refresh) {
      this.refresh
        .pipe(debounceTime(200), takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.update();
          this.autoHeight(this.fakeTextarea, 2);
        });
    }

    this.update();
  }

  ngAfterViewInit(): void {
    this.languageService.current
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((l) => {
        this.update();
      });

    this.showFake(true);

    this.control.valueChanges
      .pipe(debounceTime(200), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.autoHeight(this.el.nativeElement, 1);
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  autoHeight(nativeElement: any, padd = 2): void {
    this.renderer.setStyle(nativeElement, 'height', '0px');

    let height = nativeElement.scrollHeight;

    if (height < 38) {
      height = 38;
    }

    this.renderer.setStyle(
      nativeElement,
      'height',
      height + (22.5 + padd * 10) + 'px',
    );
  }

  @HostListener('blur') onBlur(): void {
    this.showFake();
  }

  onClick(e: any): void {
    this.showOriginal();
  }

  showFake(first = false): void {
    this.update();
    this.renderer.setProperty(this.fakeTextarea, 'hidden', false);
    this.renderer.setProperty(this.el.nativeElement, 'hidden', true);
    this.autoHeight(this.fakeTextarea, first ? 3 : 2);
  }

  showOriginal(): void {
    this.renderer.setProperty(this.fakeTextarea, 'hidden', true);
    this.renderer.setProperty(this.el.nativeElement, 'hidden', false);
    this.autoHeight(this.el.nativeElement, 1);
    this.el.nativeElement.focus();
  }

  update(val: string = this.control.value): void {
    this.changed = val;

    if (!val) {
      this.renderer.setProperty(this.fakeTextarea, 'innerHTML', this.changed);
      return;
    }

    if (typeof this.changed === 'number') {
      this.changed = String(this.changed);
    }

    if (!this.changed.match) {
      return;
    }

    const matches = this.changed.match(/\[.+?\]/g);

    if (matches) {
      const models = ['organization', 'account', 'document', 'client'];

      matches.forEach((match) => {
        let model: string;
        match = match.replace('[', '').replace(']', '');
        const original = match;

        // Transform legacy
        switch (match) {
          case 'company number':
          case 'tax number':
            match = this.camelize(match);
            break;

          case 'firstname':
          case 'lastname':
            match = 'account ' + this.camelize(match);
            break;

          case 'tax clause':
            match = 'tax_clause';
            break;

          case 'reference':
            match = 'document reference';
        }

        if (!model) {
          for (const m of models) {
            if (match.indexOf(m) !== -1) {
              model = m;
            }
          }
        }

        // Change to camel but watch out for model

        match = match.replace(' ', '.');
        const parts = match.split('=');
        const property = parts[0].split('.')[!model ? 0 : 1];

        if (!model) {
          model = 'organization';
        }

        const def = parts[1];

        this.changed = this.replace(
          this.changed,
          property,
          this[model],
          original,
          model,
        );
      });
    }

    this.renderer.setProperty(this.fakeTextarea, 'innerHTML', this.changed);
  }

  replace(
    text: string,
    property: string,
    values?: any,
    code?: string,
    model?: string,
  ): string {
    let value: string;
    let properties: any = values;

    const _props = this.definitions[model];
    if (_props) {
      ({ properties } = _props);
    }

    const name = code || property;
    const tKey = `smartcode.placeholder.${name.replace(/ /g, '.')}`;
    const t = this.translate.instant(tKey);

    const placeholder = t !== tKey ? t : name;

    // Property has no value
    if (!values) {
      value = `<span class="replaced badge-soft-light">${placeholder}</span>`;
    } else if (!values[property]) {
      let c = 'warning';

      // If property doesn't exist on object assume it's an error but could
      // be false negative
      if (!properties[property]) {
        c = 'danger';
      }

      if (this.whitelisted.indexOf(property) !== -1) {
        c = 'light';
      }

      value = `<span class="replaced badge-soft-${c}">${placeholder}</span>`;
    } else {
      const content = values[property];

      // Fallback if not string or number
      if (typeof content !== 'string' && typeof content !== 'number') {
        // content = String(content);
        // content = `[${placeholder}]`;
        return text;
      }

      value = `<span>${content}</span>`;
    }

    // Auto build code from property
    code = `\\[${!code ? property : code}\\]`;

    const rx = new RegExp(code, 'g');
    text = text.replace(rx, value);

    return text;
  }

  camelize(s: string): string {
    return s.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
      if (+match === 0) {
        return '';
      } // or if (/\s+/.test(match)) for white spaces
      return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
  }
}
